import { useTranslation } from "@helpers/useTranslation";
import { formatPoints } from "@helpers/formatPoints";
import { FirebaseCachedImage } from "@components/FirebaseCachedImage";
import { DeleteIcon } from "@components/Icons/Delete";
import { UpIcon } from "@components/Icons/Up";
import { DownIcon } from "@components/Icons/Down";
import { useEventId } from "@helpers/useEvent";
import { useState, useMemo, useEffect, useRef } from "react";
import { useSimpleCollection } from "@helpers/useSimpleCollection";
import { useTranslatedKey } from "@helpers/useTranslatedKey";

type TeamTableProps = {
  data: Team[];
  onAddPoints: (teamId: string) => void;
  onRemovePoints: (teamId: string) => void;
  onDeleteTeam: (teamId: string) => void;
};

type SortField = "name" | "totalPoints" | "points" | "additionalPoints";
type SortDirection = "asc" | "desc";

const formatTeamContactSuffix = (team: Team): string => {
  const parts = [team.email, team.phone].filter(Boolean);
  return parts.length ? ` (${parts.join(", ")})` : "";
};

export const TeamTable = ({ data, onAddPoints, onRemovePoints, onDeleteTeam }: TeamTableProps) => {
  const { t } = useTranslation("admin");
  const eventId = useEventId();
  const [sortField, setSortField] = useState<SortField>("totalPoints");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  const [manualAdjustmentsMade, setManualAdjustmentsMade] = useState(false);
  const [lockedOrder, setLockedOrder] = useState<Team[]>([]);
  const [showRightShadow, setShowRightShadow] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Fetch quiz challenges and solutions
  const [challenges] = useSimpleCollection<Challenge>(`events/${eventId}/challenges`);
  const { getTranslatedValue } = useTranslatedKey();

  const quizChallenges = useMemo(() => {
    return challenges?.filter((challenge) => challenge.type === "quiz") || [];
  }, [challenges]);

  const toggleExpanded = (teamId: string) => {
    const newExpanded = new Set(expandedTeams);
    if (newExpanded.has(teamId)) {
      newExpanded.delete(teamId);
    } else {
      newExpanded.add(teamId);
    }
    setExpandedTeams(newExpanded);
  };

  const handleManualPointAdjustment = (teamId: string, isAdd: boolean) => {
    // If this is the first manual adjustment, lock the current order
    if (!manualAdjustmentsMade) {
      setManualAdjustmentsMade(true);
      // Store the current sorted order to preserve it
      const currentSortedData = [...data].sort((a, b) => {
        let aValue: string | number;
        let bValue: string | number;

        switch (sortField) {
          case "name":
            aValue = a.displayName ?? a.name;
            bValue = b.displayName ?? b.name;
            break;
          case "totalPoints":
            aValue = (a.points || 0) + (a.additionalPoints || 0);
            bValue = (b.points || 0) + (b.additionalPoints || 0);
            break;
          case "points":
            aValue = a.points || 0;
            bValue = b.points || 0;
            break;
          case "additionalPoints":
            aValue = a.additionalPoints || 0;
            bValue = b.additionalPoints || 0;
            break;
          default:
            return 0;
        }

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sortDirection === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        } else {
          return sortDirection === "asc"
            ? (aValue as number) - (bValue as number)
            : (bValue as number) - (aValue as number);
        }
      });
      setLockedOrder(currentSortedData);
    }

    if (isAdd) {
      onAddPoints(teamId);
    } else {
      onRemovePoints(teamId);
    }
  };

  const sortedAndFilteredData = useMemo(() => {
    // If manual adjustments have been made and we're sorting by points-related fields,
    // use the locked order but update it with current data (preserving order, updating values)
    if (
      manualAdjustmentsMade &&
      (sortField === "totalPoints" || sortField === "points" || sortField === "additionalPoints")
    ) {
      // Create a map of current data by team ID for quick lookup
      const currentDataMap = new Map(data.map((team) => [team.id, team]));

      // Return locked order with updated team data
      return lockedOrder.map((lockedTeam) => currentDataMap.get(lockedTeam.id) || lockedTeam);
    }

    return [...data].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortField) {
        case "name":
          aValue = a.displayName ?? a.name;
          bValue = b.displayName ?? b.name;
          break;
        case "totalPoints":
          aValue = (a.points || 0) + (a.additionalPoints || 0);
          bValue = (b.points || 0) + (b.additionalPoints || 0);
          break;
        case "points":
          aValue = a.points || 0;
          bValue = b.points || 0;
          break;
        case "additionalPoints":
          aValue = a.additionalPoints || 0;
          bValue = b.additionalPoints || 0;
          break;
        default:
          return 0;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      } else {
        return sortDirection === "asc"
          ? (aValue as number) - (bValue as number)
          : (bValue as number) - (aValue as number);
      }
    });
  }, [data, sortField, sortDirection, manualAdjustmentsMade, lockedOrder]);

  const handleSort = (field: SortField) => {
    // If manual adjustments have been made and trying to sort by points-related fields,
    // reset the manual adjustments flag to allow sorting again
    if (manualAdjustmentsMade && (field === "totalPoints" || field === "points" || field === "additionalPoints")) {
      setManualAdjustmentsMade(false);
      setLockedOrder([]);
    }

    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;

    // Show a lock icon if manual adjustments are made and this is a points-related field
    if (manualAdjustmentsMade && (field === "totalPoints" || field === "points" || field === "additionalPoints")) {
      return <span className="ml-1 text-xs">🔒</span>;
    }

    return <span className="ml-1">{sortDirection === "asc" ? "↑" : "↓"}</span>;
  };

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    // Show right shadow if there's more content to scroll
    setShowRightShadow(scrollLeft < scrollWidth - clientWidth - 1);
  };

  // Check initial scroll state and add scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Check initial state
    handleScroll();

    // Add scroll listener
    container.addEventListener("scroll", handleScroll);

    // Check on resize
    const handleResize = () => {
      setTimeout(handleScroll, 100);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [data]); // Re-run when data changes in case table width changes

  const ChevronIcon = ({ isExpanded }: { isExpanded: boolean }) => (
    <svg
      className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );

  const QuizAnswersAccordion = ({ team }: { team: Team }) => {
    const [solutions, solutionsLoading] = useSimpleCollection<Solution>(`events/${eventId}/teams/${team.id}/solutions`);

    if (solutionsLoading) {
      return <div className="p-4 text-center text-sm opacity-50">Loading quiz answers...</div>;
    }

    const quizSolutions =
      solutions?.filter((solution) => {
        const challenge = quizChallenges.find((c) => c.id === solution.id);
        return challenge && solution.completed;
      }) || [];

    if (quizSolutions.length === 0) {
      return <div className="p-4 text-center text-sm opacity-50">{t("noQuizChallengesCompleted")}</div>;
    }

    return (
      <div className="space-y-4 bg-base-200 p-4">
        {quizSolutions.map((solution) => {
          const challenge = quizChallenges.find((c) => c.id === solution.id) as QuizChallenge;
          const quizSolution = solution as QuizSolution;

          if (!challenge || !quizSolution.answers) return null;

          const correctAnswers = challenge.questions.reduce((acc, question, index) => {
            return acc + (quizSolution.answers[index] === question.correctAnswer ? 1 : 0);
          }, 0);

          const totalQuestions = challenge.questions.length;
          const score = Math.round((correctAnswers / totalQuestions) * challenge.reward);

          return (
            <div key={solution.id} className="rounded-lg bg-base-100 p-4">
              <div className="mb-3 flex items-center justify-between">
                <h4 className="text-sm font-semibold">{getTranslatedValue(challenge, "title")}</h4>
                <div className="text-xs">
                  <span className="badge-primary badge badge-sm py-2 lowercase">
                    {correctAnswers}/{totalQuestions} {t("answered")}
                  </span>
                  <div className="mt-1 text-right font-medium text-success">
                    {score} {t("points")}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                {challenge.questions.map((question, index) => {
                  const userAnswer = quizSolution.answers[index];
                  const isCorrect = userAnswer === question.correctAnswer;

                  if (userAnswer === undefined || userAnswer === null) return null;

                  const getAnswerText = (answerIndex: number) => {
                    if (answerIndex === 0) return getTranslatedValue(question, "answer0");
                    if (answerIndex === 1) return getTranslatedValue(question, "answer1");
                    if (answerIndex === 2) return getTranslatedValue(question, "answer2");
                    if (answerIndex === 3) return getTranslatedValue(question, "answer3");
                    return "";
                  };

                  const userAnswerText = getAnswerText(userAnswer);
                  const correctAnswerText = getAnswerText(question.correctAnswer);

                  return (
                    <div key={index} className="border-l-2 py-1 pl-3 text-xs">
                      <div className="mb-1 font-medium">{getTranslatedValue(question, "question")}</div>
                      <div className="flex items-center gap-2">
                        <span className={`${isCorrect ? "text-lime-500" : "text-error"}`}>
                          {isCorrect ? "✅" : "❌"}
                        </span>
                        <span className="opacity-70">
                          {t("answered")}: {userAnswerText}
                        </span>
                      </div>
                      {!isCorrect && (
                        <div className="flex gap-2 text-lime-500">
                          <span className="invisible">✅</span>
                          <span>
                            {t("correct")}: {correctAnswerText}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="mx-1 flex flex-col justify-center p-2 pt-2 sm:mx-8 sm:px-4">
      <div className="relative">
        {showRightShadow && (
          <div className="z-2 pointer-events-none absolute right-0 top-0 -mr-1 h-full w-8 bg-gradient-to-l from-base-100 via-base-100/80 to-transparent" />
        )}

        <div className="overflow-x-auto" ref={scrollContainerRef}>
          <table className="table-compact table w-full">
            {/* head */}
            <thead>
              <tr>
                <th className="z-1 w-8 px-2 py-2 sm:px-4 sm:py-4">#</th>
                <th className="w-12 px-1 py-2 sm:px-3 sm:py-4"></th>
                <th className="px-2 py-2 sm:px-6 sm:py-4">
                  <button className="h-6 min-h-0 px-1 text-xs sm:text-sm" onClick={() => handleSort("name")}>
                    <span>{t("teamName")}</span>
                    <SortIcon field="name" />
                  </button>
                </th>
                <th className="px-2 py-2 sm:px-6 sm:py-4">
                  <button className="h-6 min-h-0 px-1 text-xs sm:text-sm" onClick={() => handleSort("totalPoints")}>
                    <span>{t("totalPoints")}</span>
                    <SortIcon field="totalPoints" />
                  </button>
                </th>
                <th className="w-20 px-1 py-2 text-xs normal-case sm:px-4 sm:py-4 sm:text-sm">{t("adjustPoints")}</th>
                <th className="w-8 px-1 py-2 normal-case sm:px-3 sm:py-4"></th>
              </tr>
            </thead>
            <tbody>
              {sortedAndFilteredData.map((team, index) => {
                const totalPoints = team.points || 0;
                const isExpanded = expandedTeams.has(team.id);

                return (
                  <>
                    <tr key={team.id}>
                      <th className="z-1 px-1 py-2 text-xs sm:px-4 sm:py-4 sm:text-sm">{index + 1}</th>
                      <th className="px-1 py-2 sm:px-3 sm:py-4">
                        <div className="avatar">
                          <div className="mask mask-squircle h-8 w-8 sm:h-12 sm:w-12">
                            <FirebaseCachedImage
                              uri={`events/${eventId}/teams/${team.id}/avatar_thumb`}
                              defaultUri="anonymous.webp"
                              alt=""
                            />
                          </div>
                        </div>
                      </th>
                      <td className="px-2 py-2 sm:px-6 sm:py-4">
                        <button
                          onClick={() => toggleExpanded(team.id)}
                          className="flex w-full items-center gap-2 rounded border-0 bg-transparent px-2 text-left transition hover:bg-base-200 sm:p-2 sm:px-4"
                          style={{ minHeight: 0 }}
                          title="View quiz answers"
                        >
                          <div className="flex-1">
                            <div className="text-sm font-bold sm:text-base lg:text-lg">
                              {`${team.displayName ?? team.name}${formatTeamContactSuffix(team)}`}
                            </div>
                            <div className="hidden text-xs opacity-50 sm:block sm:text-sm">
                              🎯 {Object.keys(team.solutionsMap || {}).length} {t("challengesCompleted")}
                              <span className="mx-2">•</span>
                              📸 {Object.keys(team.extrasMap || {}).length} {t("extras")}
                            </div>
                          </div>
                          <span className="ml-2 flex-shrink-0">
                            <ChevronIcon isExpanded={isExpanded} />
                          </span>
                        </button>
                      </td>
                      <td className="px-2 py-2 sm:px-6 sm:py-4">
                        <div className="text-sm font-bold sm:text-base lg:text-lg">{formatPoints(totalPoints)}</div>
                        {!!team.additionalPoints && (
                          <div className="text-xs sm:text-sm">
                            <span
                              className={`badge badge-xs sm:badge-sm ${
                                team.additionalPoints > 0 ? "badge-success" : "badge-warning"
                              }`}
                            >
                              {team.additionalPoints > 0 ? "+" : ""}
                              {team.additionalPoints}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-1 py-2 sm:px-4 sm:py-4">
                        <div className="flex items-center gap-1 sm:gap-2">
                          <button
                            onClick={() => handleManualPointAdjustment(team.id, false)}
                            className="btn-outline btn-xs btn h-6 min-h-0 w-6 p-0 sm:h-8 sm:w-8"
                            title={t("removePoints")}
                          >
                            <DownIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                          </button>
                          <button
                            onClick={() => handleManualPointAdjustment(team.id, true)}
                            className="btn-outline btn-xs btn h-6 min-h-0 w-6 p-0 sm:h-8 sm:w-8"
                            title={t("addPoints")}
                          >
                            <UpIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                          </button>
                        </div>
                      </td>
                      <th className="px-1 py-2 sm:px-3 sm:py-4">
                        <button
                          onClick={() => onDeleteTeam(team.id)}
                          className="btn-error btn-xs btn h-6 min-h-0 w-6 p-0 sm:h-8 sm:w-8"
                          title={t("deleteTeam")}
                        >
                          <DeleteIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                        </button>
                      </th>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={6} className="p-0">
                          <QuizAnswersAccordion team={team} />
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TeamTable;
